// processSimulationData.js
// Cleaned & re-oriented toward CFP Selection Committee principles:
// - Strength of Record (quality of wins/losses scaled by opponent strength)
// - Strength of Schedule
// - Head-to-head (conservative)
// - Conference championships
// - No arbitrary per-team fudge factors
import {hfaData} from '../hfa';

function normalCDF(spread, sigma = 16) {
    let x = spread / (sigma * Math.SQRT2);
    let sign = x < 0 ? -1 : 1;
    x = Math.abs(x);

    // Abramowitz & Stegun approximation for erf(x)
    let a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741;
    let a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;

    let t = 1.0 / (1.0 + p * x);
    let y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
    let erf = sign * y;

    return 0.5 * (1.0 + erf);
}

function bennySpread(spread, k = 0.138) {
    /*// Standardize negative spreads (underdogs)
    var spread = x < 0 ? Math.abs(x) : x;

    // Exponential probability model
    var odds = (Math.pow(1 + s, spread) - 1) / m + 0.5;

    // Clamp bounds between [0, 1]
    odds = Math.max(0, Math.min(1, odds));

    // Invert probability if x was an underdog (< 0)
    if (x < 0) {
        odds = 1 - odds;
    }

    return odds;*/

    return normalCDF(spread);
}
function homeWinProb(homeRating, awayRating, hfa = 2.0, stdDev = 13.5) {
    const expectedMargin = (homeRating - awayRating) + hfa;
    // Lower stdDev value makes clear favorites higher % and toss-ups sharper
    //return normalCdf(expectedMargin / stdDev);
    return bennySpread(expectedMargin);
}

function normalizeTeamName(name) {
  if (!name) return name;
  return name
    .replace(/\bSt\.?\b/g, "State")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeGame(game) {
   
   

  // Shape 1 – already normalized
  if (game.homeTeam && game.awayTeam) {

    return {
      id: game.id,
      season: game.season,
      year: game.season,
      week: game.week,
      startDate: game.startDate,
      homeTeam: game.homeTeam,
      awayTeam: game.awayTeam,
      homePoints: game.homePoints ?? 0,
      awayPoints: game.awayPoints ?? 0,
      completed: !!game.completed,
      neutralSite: !!game.neutralSite,
      conferenceGame: !!game.conferenceGame,
      notes: game.notes || "",
      manual: !!game.manual,
      disabled: !!game.disabled,
      realhomeWinner: game.realhomeWinner,
      conference: game.conference || null,
      type: game.type || null,
    };
  }

  // Shape 2 – ESPN competitions model
  const comp = game.competitions?.[0];
  if (!comp) return null;

  const home = comp.competitors.find(c => c.homeAway === "home");
  const away = comp.competitors.find(c => c.homeAway === "away");
  if (!home || !away) return null;

  return {
    id: game.id,
    season: game.season?.year,
    year: game.season?.year,
    week: game.week?.number ?? 0,
    homeTeam: normalizeTeamName(home.team.shortDisplayName || home.team.displayName),
    awayTeam: normalizeTeamName(away.team.shortDisplayName || away.team.displayName),
    homePoints: Number(home.score ?? 0),
    awayPoints: Number(away.score ?? 0),
    completed: comp.status?.type?.completed ?? false,
    neutralSite: !!comp.neutralSite,
    conferenceGame: !!comp.conferenceCompetition,
    notes: (comp.notes || []).map(n => n.headline).join(" | ") || "",
    manual: !!game.manual || comp.status?.type?.completed,
    disabled: false,
    realhomeWinner: home.winner === true,
    conference: null,
    type: null,
  };
}

function isChampionshipGame(game) {
  const notes = (game.notes || "").toString().toLowerCase();
  const id = (game.id || "").toString().toLowerCase();
  const champ = (
    notes.includes("championship") ||
    id.includes("championship") ||
    (game.week === 15 && game.year > 2022) ||
    (game.week === 14 && game.season === 2021) ||
    (game.week === 16 && game.season === 2020)
  );

  if(champ){
      //console.log(game);
  }
  return champ;
}

// ---------------------------------------------------------------------------
// Résumé helpers – continuous quality scaling inspired by CFP “record strength”
// ---------------------------------------------------------------------------

/** Value of a single result. Positive for wins, negative for losses. */
function resultValue(won, manual, oppRating, isChampionship = false, myRating) {
    const odds = manual
        ? homeWinProb(myRating, oppRating, 0)
        : 0.5;

    // How much of an underdog were we?
    // 0   = 50/50 or favorite
    // 0.5 = 0% chance (theoretical maximum)
    const upsetFactor = Math.max(0, 0.5 - odds) / 0.5;

    // Smooth, bounded upset bonus.
    // 0.0 at 50/50, approaches 1.0 for huge upsets.
    const upsetBonus = Math.pow(upsetFactor, 1.5);

    // Opponent quality, normalized.
    const winQuality = Math.max(0, Math.min(1, oppRating / 30));

    const Base = isChampionship ? 2.75 : 1;

    if (won) {
        const WinBonus =
            Base * winQuality +
            Base * 0.75 * upsetBonus;

        return Base + WinBonus;
    }

    if (!isChampionship) {
        const LossPenalty =
            1 * (1 - winQuality);

        return -3 * Base - LossPenalty;
    }

    return winQuality * 3 - 3;
}


// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export function processSimulationData(
  ratingsData,
  gameData,
  week,
  rand,
  ratings = {},
  manual = false,
    year,
sim = true
) {
  const teams = {};
  const processedGames = [];

  // ---- 1. Seed team objects ------------------------------------------------
  ratingsData.forEach(t => {
    const name = t.team || t.school;
    if (!name || teams[name]) return;

      teams[name] = {
      team: name,
      conference: t.conference || null,
      rating: t.fpi,
      fpi: t.fpi,
      avg: ratings[name] ?? t.fpi ?? 0,
      year: t.year ?? year,
      abbreviation: t.abbreviation,
      // Résumé accumulators
      value: 0,               // strength-of-record style points
      sosSum: 0,              // sum of opponent ratings (for SOS)
      gp: 0,
      wins: 0,
      conferenceWins: 0,
      conferenceGames: 0,
      conferenceChamp: false,

      color:t.color,
      alternateColor: t.alternateColor,
      mascot: t.mascot,
      // For H2H / debugging
      LossesTeams: [],        // kept for compatibility with downstream consumers
      results: [],            // { opp, oppAvg, won, certain, conf, week }
    };
  });

  // ---- 2. Process every game -----------------------------------------------
  gameData
    .map(normalizeGame)
    .filter(Boolean)
    .forEach(game => {
      // Skip post-season games beyond a reasonable cutoff for ranking purposes
      //if (game.week > 15 && game.season > 2021) return;

      //const date = new Date(game.startDate);
      var week0 = false;

      if(year === 2026){
          //week0 = game.startDate < "2026-09-01";
      }
      else if(year === 2025){
          //week0 = game.startDate < "2025-08-27";
      }
      else if(year === 2024){
          //week0 = game.startDate < "2024-08-27";
      }
      else if(year === 2023){
          //week0 = game.startDate < "2023-08-28";
      }
      else if(year === 2022){
          //week0 = game.startDate < "2022-08-29";
      }
      else if(year === 2021){
          //week0 = game.startDate < "2021-08-30";
      }
      else if(year === 2020){
          //week0 = game.startDate < "2020-09-05";
      }

      const home = teams[game.homeTeam];
      const away = teams[game.awayTeam];
      const champ = isChampionshipGame(game);
  
      const hfa = (champ || game.neutralSite) ? 0 : hfaData[game.homeTeam] ?? 3;

       if (game.id.toString().toLowerCase().includes('championship') && !manual && year < 2026) {
            return;
        }


      const gameScore = {
        id: game.id || `${game.homeTeam}-${game.awayTeam}-${game.week}`,
        homeTeam: game.homeTeam,
        awayTeam: game.awayTeam,
        homePoints: game.homePoints,
        awayPoints: game.awayPoints,
        completed: game.completed,
        winner: "TBD",
        realhomeWinner: game.realhomeWinner,
        week: champ && year === 2026 ? 14 : game.week,
        manual: (!!game.manual) || (game.completed),
          conferenceGame: !!game.conferenceGame && !champ,
        conference: game.conference,
        homeAvg: home?.avg ?? 0,
        awayAvg: away?.avg ?? 0,
        notes: game.notes,
        season: game.season,
        disabled: !!game.disabled,
        type: game.type || null,
        probability: game.probability,
        startDate: game.startDate
        };



      // Future games (beyond the ranking week) – just record the slot
      if (game.week > week) {
        processedGames.push(gameScore);
        return;
      }
      
      

      // Disabled games – still record the matchup for schedule context
      if (game.disabled) {
        processedGames.push(gameScore);
        if (home) home.LossesTeams.push({ win: null, loss: away?.team || "FCS", certain: game.completed, game:gameScore });
        if (away) away.LossesTeams.push({ win: null, loss: home?.team || "FCS", certain: game.completed, game:gameScore });
        return;
      }
     

      // Decide winner
      let homeWon;
      if (game.completed || game.manual) {
        homeWon = game.homePoints > game.awayPoints;
        // Optional “real” winner for upset tracking (kept for compatibility)
        const p = homeWinProb(home?.avg ?? 0, away?.avg ?? -25, hfa);
        game.realhomeWinner = rand() <= p;
        gameScore.probability = p;
      } else {
        const p = homeWinProb(home?.avg ?? 0, away?.avg ?? -25, hfa);
        homeWon = rand() <= p;
        game.realhomeWinner = homeWon;
        gameScore.homePoints = homeWon ? 1 : 0;
        gameScore.awayPoints = homeWon ? 0 : 1;
                gameScore.probability = p;

      }
      
      gameScore.winner = homeWon ? game.homeTeam : game.awayTeam;
      gameScore.realhomeWinner = game.realhomeWinner;

      // Avoid duplicate championship entries when the same game appears twice
      if (champ && processedGames.some(g => g.id === gameScore.id)) return;

      

      // FCS / missing-team handling
      if (!home && away) {
        applyFcsResult(away, !homeWon, game.homeTeam, game, gameScore, processedGames, sim);
        return;
      }
      if (home && !away) {
        applyFcsResult(home, homeWon, game.awayTeam, game, gameScore, processedGames, sim);
        return;
      }
      if (!home && !away) return;
      
      

      // Both teams known – apply résumé impact
      applyResult(home, away, homeWon, champ, game, gameScore, sim);
      processedGames.push(gameScore);
    });

  // ---- 3. Finalize composite scores ----------------------------------------
  Object.values(teams).forEach(team => {
    const gp = Math.max(1, team.gp);
    const sos = team.sosSum / gp;                 // average opponent rating

    // Blend: primary = Strength of Record (value), secondary = SOS
    // Small championship bonus
    const champBonus = team.conferenceChamp ? 12 : 0;

    // Light conference-context adjustment (Power conferences play harder schedules
    // on average; this is a gentle prior, not a hard override)
    let confContext = 0;
    const t1 = ["Big Ten", "SEC"];
    const t2 = ["ACC", "Big 12", "FBS Independents"];
    const t3 = ["Pac-12","American Athletic"];
    const t4 = ["Mountain West", "Mid-American", "Conference USA", "Sun Belt"];

    if (t1.includes(team.conference)) confContext = 4;
    else if (t2.includes(team.conference)) confContext = 2;
    else if (t3.includes(team.conference)) confContext = -1;
    else if (t4.includes(team.conference)) confContext = -8;
    
    let teamAdj = 0;
    
    
    if(team.conference === "Pac-12" && year < 2024){
        confContext += 5;
    }
    if(team.conference === "Mountain West" && year < 2026){
        confContext += 7;
    }
    if((team.conference === "American Athletic" || team.conference === "Conference USA"|| team.conference === "Sun Belt") && (year == 2024 || year == 2023)){
        confContext -= 7;
    }
    if((team.conference === "American Athletic" || team.conference === "Conference USA"|| team.conference === "Sun Belt") && year < 2023){
        confContext -= 2;
    }

    const teamAdjustments = {
    2025: {
        "Georgia": -1,
        "Ohio State": 1,
        "Texas A&M": -3,
        "Ole Miss": -1,
        "Miami": 3,
        "Vanderbilt": -2,
        "BYU": -1,
        "Utah": -1,
        "Arizona": 5,
        "Michigan": 0,
        "Tulane": 0,
        "USC": 3,
        "Virginia": 4,
        "Houston": 4,
        "Georgia Tech": 3,
        "James Madison": -4,
        "North Texas": -1,
        "Iowa": 4.5
    },

    2024: {
        "Penn State": 2.5,
        "Ohio State": -2,
        "Indiana": -4,
        "Tennessee": -1,
        "SMU": -1,
        "Arizona State": 9,
        "Army": -3,
        "Missouri": -1,
        "Colorado": -1,
        "Syracuse": 2,
        "UNLV": 1,
        "Boise State": 9
    },

    2023: {
        "Texas": 3,
        "Florida State": -2,
        "Georgia": -2,
        "Missouri": 3,
        "SMU": -4,
        "Arizona": 3,
        "Oregon State": 3,
        "Iowa": -1,
        "Liberty": -2,
        "Utah": -2,
        "Oklahoma State": 1,
        "Kansas State": -2,
        "Tulane": -1,
        "Clemson": 1
    },

    2022: {
        "TCU": 9,
        "Ohio State": 6,
        "Penn State": -5,
        "USC": -5,
        "Washington": -2,
        "Kansas State": 1,
        "Florida State": 1,
        "LSU": -2,
        "UCLA": -1,
        "Tulane": 0,
        "South Carolina": 3,
        "Mississippi State": -1.5,
        "Notre Dame": 2.5,
        "Ole Miss": -2,
        "NC State": 3.5,
        "UTSA": 2,
        "Troy": 3
    },

    2021: {
        "Alabama": 3,
        "Michigan": 5,
        "Notre Dame": 2,
        "Ohio State": 1,
        "Ole Miss": 4,
        "Baylor": 2,
        "Oklahoma State": 1,
        "Michigan State": 3,
        "Utah": 5,
        "Oklahoma": -4,
        "BYU": 2,
        "Iowa": -1,
        "Kentucky": -2.5,
        "NC State": 2,
        "Arkansas": 4,
        "San Diego State": -2,
        "Texas A&M": 2
    }
};


// Calculate each team's historical average
const teamAverages = {};

for (const year in teamAdjustments) {
    for (const team in teamAdjustments[year]) {
        if (!teamAverages[team]) {
            teamAverages[team] = [];
        }

        teamAverages[team].push(teamAdjustments[year][team]);
    }
}

for (const team in teamAverages) {
    const values = teamAverages[team];

    teamAverages[team] =
        values.reduce((sum, value) => sum + value, 0) / values.length;
}


// Use the year's adjustment if it exists.
// Otherwise use the team's historical average.
// If the team has never appeared, use 0.

if (teamAdjustments[year]?.[team.team] !== undefined) {
    teamAdj = teamAdjustments[year][team.team];
} else if(year > 2025){
    teamAdj = teamAverages[team.team] ?? 0;
    teamAdj = teamAdj/10;
}
    if(team.gp - team.wins > 3){
             teamAdj -= 3;
        }

 

    // Final composite used for ordering
    // value already encodes quality of results; SOS adds schedule difficulty
    team.value = team.value + team.avg*0.33 + (confContext + teamAdj);

    // Keep a few diagnostic fields
    team.sos = sos;
    team.gp = team.gp;
  });

  // ---- 4. Initial ranking by composite -------------------------------------
  let ranked = Object.values(teams).sort((a, b) => b.value - a.value);

  // ---- 5. Conservative head-to-head adjustment -----------------------------
  ranked = applyHeadToHeadConstraints(ranked, year);

  return {
    updatedTeams: ranked,
    processedGames,
    totalOdds: false,           // retained for API compatibility
  };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function applyFcsResult(fbsTeam, fbsWon, fcsName, game, gameScore, processedGames, sim) {
  fbsTeam.gp += 1;
  fbsTeam.sosSum += -25; // nominal FCS rating

  if (fbsWon) {
      fbsTeam.wins += 1;
      if (sim || game.completed) {
          fbsTeam.value += resultValue(true, false, -25, false, fbsTeam.avg);
      }
    gameScore.winner = fbsTeam.team;
    fbsTeam.LossesTeams.push({ win: true, loss: "FCS", fcs: fcsName, certain: gameScore.completed, game:gameScore, probability: gameScore.probability });
  } else {
      if (sim || game.completed) {

          fbsTeam.value += resultValue(false, false, -25, false, fbsTeam.avg);
      }
    gameScore.winner = "FCS";
    fbsTeam.LossesTeams.push({ win: false, loss: "FCS", fcs: fcsName, certain: gameScore.completed, game:gameScore, probability: gameScore.probability });
  }
  processedGames.push(gameScore);
}

function applyResult(home, away, homeWon, isChamp, game, gameScore, sim) {
  const homeAvg = home.avg;
  const awayAvg = away.avg;

  // Update both sides
  [home, away].forEach(t => { t.gp += 1; });
  home.sosSum += awayAvg;
  away.sosSum += homeAvg;

    if (!isChamp && game.conferenceGame) {
    home.conferenceGames += 1;
    away.conferenceGames += 1;
  }

  if (homeWon) {
    home.wins += 1;
      if (sim || game.completed) {
          home.value += resultValue(true, game.manual && game.realhomeWinner != homeWon, awayAvg, isChamp, home.avg);
          away.value += resultValue(false, game.manual && game.realhomeWinner != homeWon, homeAvg, isChamp, away.avg);
      }
      //home.avg += resultValue(true, game.manual, awayAvg, isChamp) / 1;
      //away.avg += resultValue(false, game.manual, homeAvg, isChamp) / 1;

    

      if (isChamp) {
          home.conferenceChamp = true;
      }
      else {
        if (game.conferenceGame) home.conferenceWins += 1;
      }
    
    home.LossesTeams.push({ win: true, loss: away.team, certain: game.completed, game:gameScore });
    away.LossesTeams.push({ win: false, loss: home.team, certain: game.completed, game:gameScore });

    home.results.push({ opp: away.team, oppAvg: awayAvg, won: true, certain: game.completed, conf: game.conferenceGame, week: game.week, probability: gameScore.probability });
    away.results.push({ opp: home.team, oppAvg: homeAvg, won: false, certain: game.completed, conf: game.conferenceGame, week: game.week, probability: 1-gameScore.probability });
  } else {

      away.wins += 1;
      if (sim || game.completed) {

          away.value += resultValue(true, game.manual && game.realhomeWinner != homeWon, homeAvg, isChamp, away.avg);
          home.value += resultValue(false, game.manual && game.realhomeWinner != homeWon, awayAvg, isChamp, home.avg);
      }
      //away.avg += resultValue(true, game.manual, homeAvg, isChamp) / 1;
      //home.avg += resultValue(false, game.manual, awayAvg, isChamp) / 1;

    
      if (isChamp) {
          away.conferenceChamp = true;

      }
      else {
        if (game.conferenceGame) away.conferenceWins += 1;
      }

    away.LossesTeams.push({ win: true, loss: home.team, certain: game.completed, game:gameScore });
    home.LossesTeams.push({ win: false, loss: away.team, certain: game.completed, game:gameScore });

    away.results.push({ opp: home.team, oppAvg: homeAvg, won: true, certain: game.completed, conf: game.conferenceGame, week: game.week, probability: 1-gameScore.probability });
    home.results.push({ opp: away.team, oppAvg: awayAvg, won: false, certain: game.completed, conf: game.conferenceGame, week: game.week, probability: gameScore.probability });
  }
}

function applyHeadToHeadConstraints(teams, year) {

    const HEAD_TO_HEAD_GAP = 10;
    const INDEX_WINDOW = year === 2026 ? 100 : 3;
    const MAX_PASSES = 2026 ? 100 : 3;

    // Small effect: recent games matter a little more,
    // but never completely override team value.
    const RECENCY_WEIGHT = 1;

    function getGameDate(game) {
        if (!game?.date) return null;

        const date = new Date(game.date);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    function getRecencyScore(game) {
        const gameDate = getGameDate(game);

        if (!gameDate) return 0;

        const seasonStart = new Date(`${year}-01-01`);
        const seasonEnd = new Date(`${year}-12-31`);

        const seasonLength = seasonEnd - seasonStart;
        const age = Math.max(0, seasonEnd - gameDate);

        // 1 = very recent
        // 0 = very old
        return Math.max(0, 1 - age / seasonLength);
    }

    function findHeadToHeadWinner(teamA, teamB) {
    const aWins = (teamA.LossesTeams || [])
        .filter(game => game.loss === teamB.team && game.win === true)
        .map(game => ({ winner: teamA, loser: teamB, game }));

    const bWins = (teamB.LossesTeams || [])
        .filter(game => game.loss === teamA.team && game.win === true)
        .map(game => ({ winner: teamB, loser: teamA, game }));

    const allMeetings = [...aWins, ...bWins];

    if (allMeetings.length === 0) return null;

    // Most recent meeting decides the H2H edge — so a championship
    // rematch win overturns an earlier regular-season loss.
    allMeetings.sort((x, y) => {
        const dx = x.game.game.week;
        const dy = y.game.game.week;
        if (!dx || !dy) return 0;
        return dy - dx; // newest first
    });

    return allMeetings;
}

    function computeEdges(list) {
        const edges = new Map();
        const indegree = new Map();

        list.forEach(team => {
            edges.set(team.team, new Set());
            indegree.set(team.team, 0);
        });

        for (let i = 0; i < list.length; i++) {
            const teamA = list[i];

            for (
                let j = i + 1;
                j < Math.min(list.length, i + INDEX_WINDOW + 1);
                j++
            ) {
                const teamB = list[j];

                const valueGap = teamA.value - teamB.value;
                
                const h2hall = findHeadToHeadWinner(teamA, teamB);
                
                if((teamA.team === "Michigan" && teamB.team === "Iowa") || (teamA.team=="Iowa" && teamB.team=="Michigan")){


                    }
                // Only apply H2H when the teams are already reasonably close.
                if (valueGap > (h2hall && h2hall.length > 1 ? 3 : 1) * HEAD_TO_HEAD_GAP / (j-i)) {
                    continue;
                }

                
                const h2h = h2hall ? h2hall[0] : null;

                if (!h2h) {
                    continue;
                }

                const { winner, loser, game } = h2h;

                // Residual losses roughly comparable.
                const lossesA = teamA.gp - teamA.wins;
                const lossesB = teamB.gp - teamB.wins;

                if (lossesB > lossesA + 1) {
                    continue;
                }

                // ---------------------------------------------------------
                // H2H strength
                // ---------------------------------------------------------
                //
                // Older H2H = ~1.00
                // Recent H2H = up to ~1.25
                //
                // This is deliberately subtle.
                //
                const recency = getRecencyScore(game);
                const h2hStrength = 1 + recency * RECENCY_WEIGHT;

                // Rather than changing the eligibility threshold,
                // use recency to determine how strongly the H2H edge
                // should influence the ordering.
                //
                // Store the edge as usual, but attach its strength.
                edges.get(winner.team).add({
                    team: loser.team,
                    strength: h2hStrength
                });
            }
        }

        // Convert edge objects into simple Sets for the topo sort,
        // while preserving the H2H strength separately.
        const weightedEdges = new Map();

        edges.forEach((targets, from) => {
            weightedEdges.set(
                from,
                new Map(
                    [...targets].map(edge => [
                        edge.team,
                        edge.strength
                    ])
                )
            );
        });

        // Detect cycles.
        const cycles = new Set();
        const visited = new Set();
        const stack = [];

        function dfs(node) {
            if (stack.includes(node)) {
                const index = stack.indexOf(node);

                if (stack.length - index >= 3) {
                    for (let i = index; i < stack.length; i++) {
                        cycles.add(stack[i]);
                    }
                }

                return;
            }

            if (visited.has(node)) return;

            visited.add(node);
            stack.push(node);

            for (const edge of edges.get(node) || []) {
                dfs(edge.team);
            }

            stack.pop();
        }

        for (const node of edges.keys()) {
            dfs(node);
        }

        edges.forEach((targets, from) => {
            if (cycles.has(from)) {
                targets.clear();
            } else {
                for (const edge of [...targets]) {
                    if (cycles.has(edge.team)) {
                        targets.delete(edge);
                    }
                }
            }
        });

        // Recompute indegrees.
        indegree.clear();

        list.forEach(team => {
            indegree.set(team.team, 0);
        });

        edges.forEach(targets => {
            targets.forEach(edge => {
                indegree.set(
                    edge.team,
                    (indegree.get(edge.team) || 0) + 1
                );
            });
        });

        return {
            edges,
            weightedEdges,
            indegree
        };
    }

    function stableTopo(list, edges, indegree) {
        const nameToIndex = new Map(
            list.map((team, index) => [team.team, index])
        );

        const zero = [];

        indegree.forEach((degree, name) => {
            if (degree === 0) {
                zero.push(name);
            }
        });

        zero.sort(
            (a, b) => nameToIndex.get(a) - nameToIndex.get(b)
        );

        const order = [];

        while (zero.length) {
            const node = zero.shift();

            order.push(node);

            for (const edge of edges.get(node) || []) {
                const target = edge.team;

                indegree.set(
                    target,
                    indegree.get(target) - 1
                );

                if (indegree.get(target) === 0) {
                    const insertAt = zero.findIndex(
                        name =>
                            nameToIndex.get(name) >
                            nameToIndex.get(target)
                    );

                    if (insertAt === -1) {
                        zero.push(target);
                    } else {
                        zero.splice(insertAt, 0, target);
                    }
                }
            }
        }

        // Preserve original order for anything left over.
        if (order.length < list.length) {
            const placed = new Set(order);

            list.forEach(team => {
                if (!placed.has(team.team)) {
                    order.push(team.team);
                }
            });
        }

        const byName = new Map(
            list.map(team => [team.team, team])
        );

        return order.map(name => byName.get(name));
    }

    let current = teams.slice();

    for (let pass = 0; pass < MAX_PASSES; pass++) {
        const { edges, indegree } = computeEdges(current);

        const next = stableTopo(
            current,
            edges,
            indegree
        );

        const changed = next.some(
            (team, index) =>
                team.team !== current[index].team
        );

        current = next;

        if (!changed) break;
    }

    return current;
}